import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageCostEvaluationAggregateDto,
  DamageCostEvaluationAggregateSearchPaginatedDto,
} from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type DamageCostEvaluationAggregateAutocompleteOption =
  BaseAutocompleteOption<DamageCostEvaluationAggregateDto>;

export const getDamageCostEvaluationAggregateTitle = (
  data: DamageCostEvaluationAggregateDto,
): string =>
  StringHelper.join(
    [data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber],
    ", ",
    {
      skipEmpty: true,
    },
  );

export const damageCostEvaluationAggregateToOption = (
  data: DamageCostEvaluationAggregateDto,
): DamageCostEvaluationAggregateAutocompleteOption => ({
  id: data.id!,
  title: getDamageCostEvaluationAggregateTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DamageCostEvaluationAggregateAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<DamageCostEvaluationAggregateDto> {
  searchFilters?: Partial<
    Omit<DamageCostEvaluationAggregateSearchPaginatedDto, "search" | "includeIds">
  >;
}

export default function DamageCostEvaluationAggregateAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DamageCostEvaluationAggregateAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={damageCostEvaluationAggregateToOption}
      isPreload={isPreload}
      request={{
        requestFunc:
          apiClient.damageCostEvaluationAggregatesApi
            .apiV1DamageCostEvaluationsAggregatesSearchPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          damageCostEvaluationAggregateSearchPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          damageCostEvaluationAggregateSearchPaginatedDto: {
            ...params.damageCostEvaluationAggregateSearchPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Damage cost evaluation aggregate'
      placeholder='Search...'
      renderOption={(props, option: DamageCostEvaluationAggregateAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='damageCostEvaluationAggregate' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                      {option?.data?.contract && (
                        <ContractInline entity={option.data.contract} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
